/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useCallback, useEffect } from 'react'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  toRosterPlayerRow,
  type RosterPlayerRow,
} from '@sportninja/common/features/competition/operations/utils/rosterRows'
import { sortByLastName } from '@sportninja/common/features/team/roster/utils/sortByLastName'
import { Tooltip } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import { useSelector } from 'src/hooks/useRedux'
import { InviteAction } from 'src/components/ActionPanel/ActionPanelActions'
import OptionButtonDelete from 'src/components/List/OptionButtons/Delete'
import MoreOptions from 'src/components/List/OptionButtons/More'
import RosterPlayerModal from 'src/features/team/player/RosterPlayerModal'
import { PLAYER_DETAILS_PARAM } from 'src/features/profile/player/playerDetails/constants/urlParams'
import { useModalParam } from 'src/hooks/useModalParam'
import type { ModalTrailItem } from 'src/features/profile/player/setupModal/types/modalTrail'
import { usePositionOptions } from 'src/features/team/player/hooks/useRosterPlayer'
import QuestionMarkIcon from 'src/components/List/icons/QuestionMarkIcon'
import RosterGrid from 'src/features/competition/operations/components/roster/RosterGrid'
import { useRosterPlayersColumns } from 'src/features/competition/operations/grid/useRosterPlayersColumns'
import ColumnChooser from 'src/components/ts-components/ColumnChooser'
import { useColumnVisibility } from 'src/hooks/useColumnVisibility'
import {
  applyHiddenColumns,
  chooserColumnsOf,
  ROSTER_LOCKED_COLUMN_IDS,
} from 'src/features/competition/operations/utils/rosterColumnVisibility'
import { useRosterContext } from '../context/RosterContext'
import { useUIContext } from '../context/UIContext'
import { useScrollToRow } from '../hooks/useScrollToRow'
import SectionTitle from './SectionTitle'

/** No row ever has a save in flight here — cells are read-only. */
const NO_PENDING_IDS = new Set<string>()

interface PlayerSectionProps {
  teamId: string
  /** Breadcrumb for Player Details ("Org › Team"). */
  playerTrail?: ModalTrailItem[]
  permission: Record<string, any>
  isSoccer?: boolean
  sportName?: string
  sportId?: string
}

const PlayerSection = ({
  teamId,
  playerTrail,
  permission,
  sportName = 'hockey',
  sportId,
}: PlayerSectionProps) => {
  const {
    players,
    affiliates,
    selectedRosterId,
    shouldShowWaiver,
    requireEmail,
    refresh,
    roster,
    loading,
  } = useRosterContext()
  const { highlightedRows, addHighlightedRow } = useUIContext()
  const scrollToRow = useScrollToRow()
  const isInFrame = useSelector<boolean>((state) => state.auth.inFrame)
  const positionOptions = usePositionOptions(sportId ?? null)

  // Player Details (the roster editor) is kept in the URL as the player's id
  // (?playerDetails=<id>), so a refresh or a shared link reopens it.
  const playerDetails = useModalParam(PLAYER_DETAILS_PARAM)

  const getOptions = useCallback(
    (item: any) => {
      if (!item) {
        return []
      }
      const slug = `/teams/${teamId}/rosters/${selectedRosterId}/players/${item.id}`

      const opts = [
        <MoreOptions
          key='more'
          // @ts-ignore
          options={[
            permission.update && {
              key: 'edit',
              // Opens the full-screen Player Details view (read first, edit per
              // section) for this row — labelled and iconed like the Player
              // page's Player Details button.
              title: capitalizeWords('Player Details'),
              icon: <SafetyCertificateOutlined />,
              onClick: () => playerDetails.open(String(item.id)),
            },
            // Renders as Resend Invitation or Send Invitation depending on the
            // row's invite state. Hidden on exactly the rows InviteAction
            // renders nothing for (invited AND accepted), so no empty row.
            permission.update &&
              !(item.is_invited && item.is_accepted) && {
                key: 'invite',
                Component: InviteAction,
                componentProps: {
                  asMenuItem: true,
                  invite: {
                    is_invited: item.is_invited,
                    is_accepted: item.is_accepted,
                    invitation_id: item.invitation_id,
                  },
                  user: {
                    id: item.id,
                    name_first: item.name_first,
                    name_last: item.name_last,
                    player_type_id: item.player_type.id,
                    email: item?.email,
                  },
                  slug,
                  onComplete: async () => await refresh(),
                },
              },
            permission.update && {
              key: 'remove',
              Component: OptionButtonDelete,
              componentProps: {
                buttonText: 'remove',
                slug,
                itemId: item.id,
                iconName: 'user-minus',
                title: `Remove ${item.name_first} ${item.name_last}`,
                type: t('common:player', {}),
                onComplete: refresh,
              },
            },
          ].filter(Boolean)}
        />,
      ]

      return opts.filter(Boolean)
    },
    [teamId, selectedRosterId, permission, refresh, playerDetails]
  )

  /** The raw context row for an id — the menu's flows need the full object. */
  const rawPlayer = (id: string) =>
    [...players, ...affiliates].find(
      (player: { id: string }) => player.id === id
    )

  // The row Player Details edits, looked up in the loaded roster.
  const editPlayerRow = playerDetails.value
    ? rawPlayer(playerDetails.value) ?? null
    : null
  const rosterLoaded = Boolean(roster) && !loading

  // A link to someone not on this roster has nothing to open: drop it.
  useEffect(() => {
    if (playerDetails.value && rosterLoaded && !editPlayerRow) {
      playerDetails.close()
    }
  }, [playerDetails, rosterLoaded, editPlayerRow])

  // Same columns, order and titles as the Operations roster grid. Cells stay
  // read-only (no `onEditField`) and the Team screen keeps its own menu.
  const columns = useRosterPlayersColumns({
    canEdit: Boolean(permission?.update || permission?.admin),
    showWaiver: !isInFrame && shouldShowWaiver,
    showEmail: Boolean(permission?.update),
    showInvite: !isInFrame,
    positionOptions,
    renderActions: (row: RosterPlayerRow) => (
      <div css={actionsStyle}>{getOptions(rawPlayer(row.id))}</div>
    ),
    profileLinkOf: (row: RosterPlayerRow) => `${ROUTES.PLAYER_ROOT}/${row.id}`,
    showCustomerIdentifier: true,
  })

  // Same chooser as the Operations roster. No storageKey: the choice lasts for
  // this visit only and resets on refresh, as it does there.
  const visibility = useColumnVisibility({
    columns: chooserColumnsOf(columns, ROSTER_LOCKED_COLUMN_IDS),
  })
  const shownColumns = applyHiddenColumns(columns, visibility.isHidden)

  // Last name first by default, as the Team screen has always listed them.
  const playerRows: RosterPlayerRow[] =
    sortByLastName(players).map(toRosterPlayerRow)
  const spareRows: RosterPlayerRow[] =
    sortByLastName(affiliates).map(toRosterPlayerRow)
  const highlightedIds = new Set<string>(highlightedRows.keys())

  if (playerRows.length === 0 && spareRows.length === 0) {
    return (
      <div>
        <SectionTitle>Players</SectionTitle>
        <p
          css={css`
            color: ${colors.NEUTRAL_50};
            font-size: 14px;
          `}
        >
          No players have been added to this roster yet.
        </p>
      </div>
    )
  }

  return (
    <>
      <div
        css={css`
          width: 100%;
          max-width: 100%;
          min-width: 0;
        `}
      >
        <SectionTitle
          action={
            <ColumnChooser
              label={capitalizeWords(t('common:gameGridColumnsButton', {}))}
              title={capitalizeWords(t('common:gameGridColumnsTitle', {}))}
              columns={visibility.columns}
              onToggle={visibility.setColumnVisible}
              onReset={visibility.resetColumns}
              data-testid='team-roster-players-column-chooser'
            />
          }
        >
          Players
        </SectionTitle>
        {playerRows.length === 0 && spareRows.length > 0 ? null : (
          <RosterGrid
            rows={playerRows}
            columns={shownColumns}
            pendingIds={NO_PENDING_IDS}
            highlightedIds={highlightedIds}
            loading={false}
            testId='team-roster-players'
          />
        )}

        {spareRows.length > 0 && (
          <>
            <div
              css={css`
                margin-top: 24px;
                margin-bottom: 16px;
                font-weight: 700;
                color: ${colors.DEFAULT_FLAIR};
                text-transform: uppercase;
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <span>Spares</span>
              <Tooltip
                title='Spares are not full team members and cannot access attendance or team chat.'
                placement='top'
              >
                <span
                  css={css`
                    cursor: help;
                    display: flex;
                    align-items: center;
                    margin-bottom: 2px;
                  `}
                >
                  <QuestionMarkIcon width={20} height={20} />
                </span>
              </Tooltip>
            </div>
            <RosterGrid
              rows={spareRows}
              columns={shownColumns}
              pendingIds={NO_PENDING_IDS}
              highlightedIds={highlightedIds}
              loading={false}
              testId='team-roster-spares'
            />
          </>
        )}
      </div>
      {selectedRosterId ? (
        <RosterPlayerModal
          isOpen={Boolean(editPlayerRow)}
          editPlayer={editPlayerRow || undefined}
          onClose={playerDetails.close}
          onSuccess={async () => {
            await refresh()
            if (editPlayerRow?.id) {
              addHighlightedRow(editPlayerRow.id, 'success')
              scrollToRow(editPlayerRow.id)
            }
            playerDetails.close()
          }}
          teamId={teamId}
          rosterId={selectedRosterId}
          sportId={sportId ?? null}
          sportName={sportName}
          hasAdminPermission={permission?.admin}
          requireEmail={requireEmail}
          familyMembers
          trail={playerTrail}
        />
      ) : null}
    </>
  )
}

/* Centres the ⋯ trigger in the pinned actions column. */
const actionsStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`

export default PlayerSection
